/**
 * Error thrown by {@link request} when the response status is not ok. Carries
 * the HTTP status so callers can branch (e.g. renew the token + retry on 401).
 */
export class HttpError extends Error {
	constructor(
		public readonly status: number,
		public readonly statusText: string,
		public readonly url: string
	) {
		super(`Request failed: ${status} ${statusText}`);
		this.name = 'HttpError';
	}
}

type RequestOptions = {
	method: string;
	token: string | null | undefined;
	body?: Record<string, unknown>;
};

/**
 * Shared fetch core: attaches the Bearer token, throws {@link HttpError} on a
 * non-ok response (never swallows failures), and parses the JSON body
 * (tolerating an empty body). Both getFetch and postFetch build on this so the
 * two share one error contract.
 */
export const request = async <T>(
	url: string,
	{ method, token, body }: RequestOptions
): Promise<T> => {
	const headers = new Headers();

	if (body !== undefined) headers.append('Content-Type', 'application/json');
	if (token) headers.append('Authorization', `Bearer ${token}`);

	const response = await fetch(url, {
		method,
		headers,
		body: body !== undefined ? JSON.stringify(body) : undefined,
		redirect: 'follow',
	});

	if (!response.ok) {
		throw new HttpError(response.status, response.statusText, url);
	}

	const text = await response.text();

	return (text ? JSON.parse(text) : undefined) as T;
};
